import React from 'react';
import { DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { Popconfirm } from 'antd';
import { useMemo, useState } from 'react';
import styles from './SessionHistoryPanel.less';

export interface HistoryItem {
  id: string;
  title: string;
  lastActive: number;
  isViewing: boolean;
  deletable: boolean;
  /** 删除按钮 disabled 时的提示文案 */
  undeletableReason?: string;
}

interface Props {
  items: HistoryItem[];
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onCloseOverlay: () => void;
}

const relativeTime = (ts: number): string => {
  if (!ts) return '';
  const diff = Date.now() - ts;
  const min = Math.floor(diff / 60_000);
  if (min < 1) return '刚刚';
  if (min < 60) return `${min} 分钟前`;
  const hour = Math.floor(min / 60);
  if (hour < 24) return `${hour} 小时前`;
  const day = Math.floor(hour / 24);
  if (day < 7) return `${day} 天前`;
  const d = new Date(ts);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
};

const cx = (...names: (string | false | undefined)[]) => names.filter(Boolean).join(' ');

export default function SessionHistoryPanel({ items, onSelect, onDelete, onCloseOverlay }: Props) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const kw = search.trim().toLowerCase();
    if (!kw) return items;
    return items.filter((it) => it.title.toLowerCase().includes(kw));
  }, [items, search]);

  return (
    <>
      <div onClick={onCloseOverlay} className={styles.overlay} />
      <div className={styles.panel}>
        <div className={styles.title}>历史对话</div>
        <div className={styles.searchBox}>
          <SearchOutlined className={styles.searchIcon} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="请输入搜索关键词"
            className={styles.searchInput}
          />
        </div>

        {filtered.length === 0 ? (
          <div className={styles.empty}>{search.trim() ? '无匹配会话' : '暂无历史对话'}</div>
        ) : (
          <div className={styles.list}>
            {filtered.map((it) => (
              <div
                key={it.id}
                onClick={() => onSelect(it.id)}
                className={cx(styles.row, it.isViewing && styles.viewing)}
              >
                <div className={styles.rowMain}>
                  <div className={styles.rowTitle} title={it.title}>
                    {it.title || '新会话'}
                  </div>
                  <div className={styles.rowTime}>{relativeTime(it.lastActive)}</div>
                </div>
                {it.deletable ? (
                  <Popconfirm
                    title="删除此会话？"
                    description="删除后将无法恢复"
                    okText="删除"
                    cancelText="取消"
                    okButtonProps={{ danger: true, size: 'small' }}
                    cancelButtonProps={{ size: 'small' }}
                    overlayStyle={{ zIndex: 1200 }}
                    onConfirm={(e) => {
                      e?.stopPropagation();
                      onDelete(it.id);
                    }}
                    onCancel={(e) => e?.stopPropagation()}
                  >
                    <span onClick={(e) => e.stopPropagation()} title="删除" className={styles.delBtn}>
                      <DeleteOutlined style={{ fontSize: 13 }} />
                    </span>
                  </Popconfirm>
                ) : (
                  <span
                    title={it.undeletableReason}
                    onClick={(e) => e.stopPropagation()}
                    className={styles.delBtnDisabled}
                  >
                    <DeleteOutlined style={{ fontSize: 13 }} />
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
